/**
 * Utilitário para Google Analytics
 * Funciona em produção e ambientes de teste configurados
 */

// Lista de hostnames permitidos para carregar o Google Analytics
const getAllowedHostnames = (): string[] => {
  return [
    'app.seulugar.imb.br', // produção
    'seulugar.homolog.youxgroup.com.br', // homologação/teste
    'seulugar.qas.youxgroup.com.br', // QAS/teste
  ]
}

// Verifica se está em um ambiente permitido
const isAllowedEnvironment = (): boolean => {
  return getAllowedHostnames().includes(window.location.hostname)
}

// Verifica se está em ambiente de produção
const isProduction = (): boolean => {
  return window.location.hostname === 'app.seulugar.imb.br'
}

// Verifica se o Google Analytics está disponível
const isGAAvailable = (): boolean => {
  return isAllowedEnvironment() && typeof window.gtag !== 'undefined'
}

/**
 * Rastreia uma visualização de página
 * @param url - URL da página a ser rastreada
 */
export const trackPageView = (url: string): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('config', 'G-FZSX6MDGXJ', {
      page_path: url
    })
  }
}

/**
 * Rastreia um evento customizado
 * @param eventName - Nome do evento
 * @param eventParams - Parâmetros adicionais do evento
 *
 * @example
 * trackEvent('button_click', { button_name: 'login', page: '/login' })
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('event', eventName, eventParams)
  }
}

/**
 * Rastreia uma conversão
 * @param conversionId - ID da conversão
 * @param conversionParams - Parâmetros da conversão
 */
export const trackConversion = (
  conversionId: string,
  conversionParams?: Record<string, any>
): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: conversionId,
      ...conversionParams
    })
  }
}

/**
 * Rastreia uma exceção/erro
 * @param description - Descrição do erro
 * @param fatal - Se o erro é fatal (padrão: false)
 */
export const trackException = (
  description: string,
  fatal: boolean = false
): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('event', 'exception', {
      description,
      fatal
    })
  }
}

/**
 * Define uma propriedade de usuário
 * @param userId - ID do usuário
 */
export const setUserId = (userId: string | number): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('config', 'G-FZSX6MDGXJ', {
      user_id: userId.toString()
    })
  }
}

/**
 * Define propriedades customizadas do usuário
 * @param properties - Objeto com as propriedades do usuário
 *
 * @example
 * setUserProperties({ user_type: 'admin', plan: 'premium' })
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('set', 'user_properties', properties)
  }
}

/**
 * Rastreia tempo de carregamento
 * @param name - Nome da métrica
 * @param value - Valor em milissegundos
 * @param category - Categoria da métrica
 */
export const trackTiming = (
  name: string,
  value: number,
  category?: string
): void => {
  if (isGAAvailable() && window.gtag) {
    window.gtag('event', 'timing_complete', {
      name,
      value,
      event_category: category
    })
  }
}

/**
 * Verifica se o Google Analytics está habilitado
 * @returns true se estiver em um ambiente permitido e o GA estiver carregado
 */
export const isAnalyticsEnabled = (): boolean => {
  return isGAAvailable()
}

// Exporta objeto com todas as funções
export const GoogleAnalytics = {
  trackPageView,
  trackEvent,
  trackConversion,
  trackException,
  setUserId,
  setUserProperties,
  trackTiming,
  isEnabled: isAnalyticsEnabled
}

export default GoogleAnalytics
